/**
 * A list view: one line per member, showing the lists it belongs to.
 *
 * Two columns and no others, which is what makes this view type different from
 * every other one here -- it does not draw the fields the view shows, it draws
 * the two the view *names*. The field options are hidden by default for exactly
 * that reason: they say nothing about a list.
 *
 * The rows come through the same window as everywhere else, and what goes in
 * the two columns is `list/values`, which the MCP App's list shell has been
 * using since M10. Written there rather than here on purpose: a list view in a
 * chat and a list view on this page should say the same thing about the same
 * row, and the only way to be sure of that is for them to run the same code.
 */
import { useEffect, useRef, type UIEvent } from 'react'
import { useTranslation } from 'react-i18next'

import type { Field, ViewOf } from '@/api/types'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/shared/ui/empty'
import { rowColors } from '../decorations/colors'
import { selectColorStyle } from '../fields/select-options'
import { useFieldContext } from '../fields/ui/context'
import { RowModal } from '../row/row-modal'
import {
  useRegisteredRowWindow,
  useRowQuery,
  useRowWindow,
  useRowWindowStore,
} from '../grid/use-grid'
import type { RowSearch } from '../rows/rowQuery'
import { rowAt } from '../rows/rowWindow'
import { orderFields, type FieldPlacement } from '../views/field-order'
import { useRowDialog } from '../views/use-row-dialog'
import { fetchListRows } from './fetcher'
import { isListViewConfigured, listSettings, listValues, memberValue } from './values'

/** One line, which is one row: fixed, so the list can be virtualised. */
const ROW_HEIGHT = 44

export interface ListViewProps {
  view: ViewOf<'list'>
  fields: readonly Field[]
  fieldOptions: Record<string, FieldPlacement>
  search: RowSearch
  openRowId: number | null
  onOpenRow: (rowId: number | null) => void
}

export function ListView({
  view,
  fields,
  fieldOptions,
  search,
  openRowId,
  onOpenRow,
}: ListViewProps) {
  const { t } = useTranslation()
  const store = useRowWindowStore(fetchListRows)
  useRowQuery(store, view, search)
  const member = useRegisteredRowWindow(store, {
    tableId: view.table_id,
    viewId: view.id,
    fields,
    view,
  })

  const context = useFieldContext(fields[0]?.workspace_id)
  const scroller = useRef<HTMLDivElement>(null)

  const count = useRowWindow(store, (state) => state.window.count)
  const answered = useRowWindow(store, (state) => state.answered)
  const visible = useRowWindow(store, (state) => state.visible)
  const window = useRowWindow(store, (state) => state.window)

  useEffect(() => {
    const element = scroller.current
    if (element === null) return
    const report = () => {
      store
        .getState()
        .setViewport({ height: element.clientHeight, rowHeight: ROW_HEIGHT })
    }
    report()
    const observer = new ResizeObserver(report)
    observer.observe(element)
    return () => {
      observer.disconnect()
    }
  }, [store])

  const openRow = useRowDialog({
    window,
    count,
    member,
    view,
    fields,
    openRowId,
    onOpenRow,
    search,
  })

  const settings = listSettings(view)
  const memberField =
    fields.find((field) => field.id === settings.member_field_id) ?? null
  const listField = fields.find((field) => field.id === settings.list_field_id) ?? null

  // The backend stores a list view before either column has been chosen, so
  // this is a state to answer for rather than an error. A column the view names
  // and the table no longer has counts too: the field can be deleted after the
  // view was configured.
  if (!isListViewConfigured(settings) || memberField === null || listField === null) {
    return (
      <Empty className="p-6">
        <EmptyHeader>
          <EmptyTitle>{t('list.unconfiguredTitle')}</EmptyTitle>
          <EmptyDescription>{t('list.unconfiguredDescription')}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    )
  }

  const decorations = view.decorations ?? []
  const indexes: number[] = []
  for (let index = visible.start; index < visible.end; index += 1) indexes.push(index)

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        className="bg-muted/50 text-muted-foreground grid shrink-0 grid-cols-[minmax(8rem,1fr)_2fr] border-b text-xs font-medium"
        aria-hidden
      >
        <div className="truncate border-r p-2">{memberField.name}</div>
        <div className="truncate p-2">{listField.name}</div>
      </div>

      <div
        ref={scroller}
        onScroll={(event: UIEvent<HTMLDivElement>) => {
          store.getState().scrolled(event.currentTarget.scrollTop)
        }}
        className="min-h-0 flex-1 overflow-auto"
        role="table"
        aria-label={t('list.label')}
        aria-rowcount={count}
        aria-colcount={2}
      >
        {count === 0 && answered ? (
          <Empty className="p-6">
            <EmptyHeader>
              <EmptyTitle>{t('list.emptyTitle')}</EmptyTitle>
              <EmptyDescription>{t('list.emptyDescription')}</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className="relative" style={{ height: count * ROW_HEIGHT }}>
            {indexes.map((index) => {
              const row = rowAt(window, index)
              if (row === undefined) {
                return (
                  <div
                    key={index}
                    data-testid={`list-placeholder-${index}`}
                    className="absolute inset-x-0 border-b p-2"
                    style={{ top: index * ROW_HEIGHT, height: ROW_HEIGHT }}
                  >
                    <div className="bg-muted/40 h-full w-1/3 animate-pulse rounded" />
                  </div>
                )
              }
              const colors = rowColors(decorations, row, fields, context)
              return (
                <button
                  key={row.id}
                  type="button"
                  role="row"
                  aria-rowindex={index + 1}
                  data-testid={`list-row-${row.id}`}
                  onClick={() => {
                    onOpenRow(row.id)
                  }}
                  className="hover:bg-muted/30 focus-visible:ring-ring absolute inset-x-0 grid grid-cols-[minmax(8rem,1fr)_2fr] border-b text-left text-sm focus-visible:ring-2 focus-visible:outline-none"
                  style={{
                    top: index * ROW_HEIGHT,
                    height: ROW_HEIGHT,
                    ...(colors.background === null
                      ? {}
                      : { backgroundColor: colors.background }),
                    ...(colors.leftBorder === null
                      ? {}
                      : { borderLeft: `4px solid ${colors.leftBorder}` }),
                  }}
                >
                  <div role="cell" className="flex items-center truncate border-r p-2">
                    {memberValue(row, settings, fields, context)}
                  </div>
                  <div role="cell" className="flex flex-wrap items-center gap-1 p-2">
                    {listValues(row, settings, fields).map((value) => (
                      <span
                        key={value.id}
                        className="max-w-full truncate rounded px-1.5 py-0.5 text-xs"
                        style={selectColorStyle(value.color)}
                      >
                        {value.label}
                      </span>
                    ))}
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>

      {openRowId !== null && (
        <RowModal
          member={openRow.member}
          rowId={openRowId}
          row={openRow.row}
          missing={openRow.missing}
          // A list names two columns and shows no others, so "the fields it
          // shows" is not the question the dialog is asking: it opens the whole
          // row, which is every field the table has.
          fields={orderFields(fields, fieldOptions)}
          hidden={[]}
          context={context}
          steps={openRow.steps}
          onStep={openRow.onStep}
          onClose={() => {
            onOpenRow(null)
          }}
        />
      )}
    </div>
  )
}
